/**
 * SPF / DMARC record and email signature generators.
 *
 * The differentiator here is the SPF DNS-lookup counter. RFC 7208 caps an SPF
 * evaluation at 10 DNS-querying mechanisms; exceeding it is a permerror, which
 * means SPF fails — and because the record still *exists*, every
 * "does this domain have SPF?" checker reports a cheerful green tick while
 * mail quietly fails DMARC. Counting the lookups is the whole point.
 *
 * Pure functions, no I/O. In @aegis/shared so the generators can run in the
 * browser as well as on the server.
 */

export interface SpfProvider {
  id: string;
  label: string;
  /** The include: mechanism to add. */
  include: string;
  /**
   * DNS lookups this include costs in practice — an include is 1 lookup plus
   * whatever the target record nests. Measured values, not guesses of 1.
   */
  lookups: number;
}

/** Providers we see most often on customer domains. */
export const SPF_PROVIDERS: SpfProvider[] = [
  { id: 'google', label: 'Google Workspace', include: '_spf.google.com', lookups: 4 },
  { id: 'microsoft365', label: 'Microsoft 365', include: 'spf.protection.outlook.com', lookups: 2 },
  { id: 'zoho', label: 'Zoho Mail', include: 'zoho.com', lookups: 2 },
  { id: 'gandi', label: 'Gandi Mail', include: '_mailcust.gandi.net', lookups: 2 },
  { id: 'fastmail', label: 'Fastmail', include: 'spf.messagingengine.com', lookups: 2 },
  { id: 'sendgrid', label: 'SendGrid', include: 'sendgrid.net', lookups: 3 },
  { id: 'mailgun', label: 'Mailgun', include: 'mailgun.org', lookups: 2 },
  { id: 'amazonses', label: 'Amazon SES', include: 'amazonses.com', lookups: 1 },
  { id: 'postmark', label: 'Postmark', include: 'spf.mtasv.net', lookups: 1 },
  { id: 'brevo', label: 'Brevo', include: 'spf.brevo.com', lookups: 2 },
  { id: 'mailchimp', label: 'Mailchimp', include: 'servers.mcsv.net', lookups: 1 },
  { id: 'hubspot', label: 'HubSpot', include: '_spf.hubspotemail.net', lookups: 3 },
  { id: 'salesforce', label: 'Salesforce', include: '_spf.salesforce.com', lookups: 2 },
  { id: 'zendesk', label: 'Zendesk', include: 'mail.zendesk.com', lookups: 1 },
  { id: 'freshdesk', label: 'Freshdesk', include: 'email.freshdesk.com', lookups: 1 },
];

/** RFC 7208 §4.6.4 — evaluation must not exceed 10 DNS-querying mechanisms. */
export const SPF_MAX_LOOKUPS = 10;

export interface SpfGeneratorInput {
  /** Provider ids from SPF_PROVIDERS. */
  providers?: string[];
  /** Additional raw include: targets. */
  extraIncludes?: string[];
  /** IPv4 addresses or CIDR blocks — these cost no DNS lookups. */
  ip4?: string[];
  /** IPv6 addresses or CIDR blocks — also free. */
  ip6?: string[];
  /** Whether to keep the domain's own MX hosts authorized (costs 1 lookup). */
  includeMx?: boolean;
  /** Whether to authorize the domain's A record (costs 1 lookup). */
  includeA?: boolean;
  /** '-all' (hard fail) is correct for a domain you fully control. */
  policy?: '-all' | '~all' | '?all';
}

export interface SpfGeneratorResult {
  record: string;
  host: string;
  type: 'TXT';
  lookup_count: number;
  lookup_budget: number;
  over_limit: boolean;
  warnings: string[];
  /** Per-mechanism lookup attribution, so a user can see what to cut. */
  breakdown: { mechanism: string; lookups: number }[];
}

export function generateSpfRecord(input: SpfGeneratorInput): SpfGeneratorResult {
  const {
    providers = [],
    extraIncludes = [],
    ip4 = [],
    ip6 = [],
    includeMx = false,
    includeA = false,
    policy = '~all',
  } = input;

  const parts: string[] = ['v=spf1'];
  const breakdown: { mechanism: string; lookups: number }[] = [];
  const warnings: string[] = [];

  if (includeA) {
    parts.push('a');
    breakdown.push({ mechanism: 'a', lookups: 1 });
  }
  if (includeMx) {
    parts.push('mx');
    breakdown.push({ mechanism: 'mx', lookups: 1 });
  }

  // ip4/ip6 are free — always prefer them over includes when possible.
  for (const ip of ip4) {
    parts.push(`ip4:${ip}`);
    breakdown.push({ mechanism: `ip4:${ip}`, lookups: 0 });
  }
  for (const ip of ip6) {
    parts.push(`ip6:${ip}`);
    breakdown.push({ mechanism: `ip6:${ip}`, lookups: 0 });
  }

  const seen = new Set<string>();
  for (const id of providers) {
    const provider = SPF_PROVIDERS.find((p) => p.id === id);
    if (!provider) {
      warnings.push(`Unknown provider "${id}" — skipped.`);
      continue;
    }
    if (seen.has(provider.include)) continue;
    seen.add(provider.include);
    parts.push(`include:${provider.include}`);
    breakdown.push({ mechanism: `include:${provider.include}`, lookups: provider.lookups });
  }

  for (const include of extraIncludes) {
    const cleaned = include.replace(/^include:/, '').trim();
    if (!cleaned || seen.has(cleaned)) continue;
    seen.add(cleaned);
    parts.push(`include:${cleaned}`);
    // Unknown third-party includes cost at least 1 and often nest further —
    // assume 1 but say so, rather than silently under-counting.
    breakdown.push({ mechanism: `include:${cleaned}`, lookups: 1 });
  }

  parts.push(policy);
  const record = parts.join(' ');
  const lookupCount = breakdown.reduce((sum, b) => sum + b.lookups, 0);
  const overLimit = lookupCount > SPF_MAX_LOOKUPS;

  if (overLimit) {
    warnings.push(
      `This record needs ${lookupCount} DNS lookups but SPF allows only ${SPF_MAX_LOOKUPS}. ` +
        'Evaluation returns permerror, SPF fails, and DMARC fails with it — even though the record exists. ' +
        'Replace the heaviest includes with ip4: ranges, or use an SPF flattening service.',
    );
  } else if (lookupCount >= 8) {
    warnings.push(
      `${lookupCount} of ${SPF_MAX_LOOKUPS} DNS lookups used. Adding one more provider will likely break SPF.`,
    );
  }

  if (extraIncludes.length > 0) {
    warnings.push(
      'Custom includes are counted as 1 lookup each. Nested includes cost more — verify the real total after publishing.',
    );
  }
  if (policy === '?all') {
    warnings.push('A neutral policy (?all) authorizes nothing. Use ~all while testing, then -all.');
  }
  if (record.length > 255) {
    warnings.push(
      `The record is ${record.length} characters. A single TXT string is capped at 255; publish it as multiple ` +
        'quoted strings in one record (most DNS hosts do this automatically).',
    );
  }

  return {
    record,
    host: '@',
    type: 'TXT',
    lookup_count: lookupCount,
    lookup_budget: SPF_MAX_LOOKUPS,
    over_limit: overLimit,
    warnings,
    breakdown,
  };
}

export interface DmarcGeneratorInput {
  policy: 'none' | 'quarantine' | 'reject';
  /** Aggregate report destination — without this, DMARC gives you no data. */
  rua?: string;
  /** Forensic report destination. */
  ruf?: string;
  /** Percentage of mail the policy applies to (1-100). */
  pct?: number;
  /** Subdomain policy, if different from the main one. */
  sp?: 'none' | 'quarantine' | 'reject';
  /** DKIM alignment: 'r' relaxed (default), 's' strict. */
  adkim?: 'r' | 's';
  /** SPF alignment: 'r' relaxed (default), 's' strict. */
  aspf?: 'r' | 's';
}

export interface DmarcGeneratorResult {
  record: string;
  host: string;
  type: 'TXT';
  warnings: string[];
}

export function generateDmarcRecord(input: DmarcGeneratorInput): DmarcGeneratorResult {
  const { policy, rua, ruf, pct, sp, adkim, aspf } = input;
  const warnings: string[] = [];

  const parts = ['v=DMARC1', `p=${policy}`];
  if (sp) parts.push(`sp=${sp}`);
  if (rua) parts.push(`rua=${normalizeReportUri(rua, warnings, 'rua')}`);
  if (ruf) parts.push(`ruf=${normalizeReportUri(ruf, warnings, 'ruf')}`);
  if (pct !== undefined && pct !== 100) {
    if (pct < 1 || pct > 100) {
      warnings.push(`pct must be between 1 and 100; got ${pct}. Omitted.`);
    } else {
      parts.push(`pct=${pct}`);
    }
  }
  if (adkim) parts.push(`adkim=${adkim}`);
  if (aspf) parts.push(`aspf=${aspf}`);

  if (!rua) {
    warnings.push(
      'No rua= address. Without aggregate reports DMARC is invisible — you cannot tell whether ' +
        'a stricter policy would block your own legitimate mail.',
    );
  }
  if (policy === 'none') {
    warnings.push(
      'p=none only monitors; it does not protect the domain and does not qualify for BIMI. ' +
        'Move to quarantine once reports look clean.',
    );
  }
  if (policy === 'reject' && (pct === undefined || pct === 100)) {
    warnings.push(
      'p=reject at 100% is the right destination, but roll out gradually (pct=25 → 50 → 100) ' +
        'and watch reports — anything not aligned will be rejected outright.',
    );
  }
  if (ruf) {
    warnings.push(
      'Forensic reports (ruf) contain message content and most large providers never send them. ' +
        'rua is what you actually need.',
    );
  }

  return { record: parts.join('; '), host: '_dmarc', type: 'TXT', warnings };
}

function normalizeReportUri(value: string, warnings: string[], tag: string): string {
  return value
    .split(',')
    .map((entry) => {
      const trimmed = entry.trim();
      if (!trimmed) return '';
      if (trimmed.startsWith('mailto:')) return trimmed;
      if (trimmed.includes('@')) return `mailto:${trimmed}`;
      warnings.push(`${tag} entry "${trimmed}" is not an email address or mailto: URI.`);
      return trimmed;
    })
    .filter(Boolean)
    .join(',');
}

export interface SignatureInput {
  name: string;
  title?: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  /** Rendered as text links; no remote images, deliberately. */
  links?: { label: string; url: string }[];
  accentColor?: string;
}

export interface SignatureResult {
  html: string;
  text: string;
  warnings: string[];
}

/** Minimal HTML escaping — signatures are user input rendered into markup. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function safeUrl(value: string): string | null {
  const trimmed = value.trim();
  // Block javascript:/data: and anything else that is not plain web/mail.
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
  if (/^[\w.-]+\.[a-z]{2,}(\/|$)/i.test(trimmed)) return `https://${trimmed}`;
  return null;
}

/**
 * Generate a signature that will not hurt deliverability.
 *
 * Table-free, image-free and tracker-free on purpose: remote images in a
 * signature are the most common reason an otherwise plain business email gets
 * flagged, and they break in every client that blocks images by default.
 */
export function generateEmailSignatureHtml(input: SignatureInput): SignatureResult {
  const { name, title, company, email, phone, website, links = [], accentColor = '#1a56db' } = input;
  const warnings: string[] = [];

  const accent = /^#[0-9a-f]{3,8}$/i.test(accentColor) ? accentColor : '#1a56db';
  if (accent !== accentColor) warnings.push('Accent colour was not a valid hex value; used the default.');

  const rows: string[] = [];
  rows.push(
    `<div style="font-weight:600;font-size:14px;color:#111827;">${escapeHtml(name)}</div>`,
  );

  const roleLine = [title, company].filter(Boolean).map((v) => escapeHtml(v!)).join(' · ');
  if (roleLine) {
    rows.push(`<div style="font-size:13px;color:#4b5563;">${roleLine}</div>`);
  }

  const contactBits: string[] = [];
  if (email) {
    contactBits.push(
      `<a href="mailto:${escapeHtml(email)}" style="color:${accent};text-decoration:none;">${escapeHtml(email)}</a>`,
    );
  }
  if (phone) contactBits.push(escapeHtml(phone));
  if (website) {
    const url = safeUrl(website);
    if (url) {
      contactBits.push(
        `<a href="${escapeHtml(url)}" style="color:${accent};text-decoration:none;">${escapeHtml(
          website.replace(/^https?:\/\//i, ''),
        )}</a>`,
      );
    } else {
      warnings.push(`Website "${website}" is not a valid URL and was omitted.`);
    }
  }
  if (contactBits.length) {
    rows.push(`<div style="font-size:13px;color:#4b5563;">${contactBits.join(' &nbsp;|&nbsp; ')}</div>`);
  }

  const linkBits = links
    .map(({ label, url }) => {
      const safe = safeUrl(url);
      if (!safe) {
        warnings.push(`Link "${label}" has an unsupported URL and was omitted.`);
        return null;
      }
      return `<a href="${escapeHtml(safe)}" style="color:${accent};text-decoration:none;">${escapeHtml(label)}</a>`;
    })
    .filter(Boolean);
  if (linkBits.length) {
    rows.push(`<div style="font-size:13px;">${linkBits.join(' &nbsp;·&nbsp; ')}</div>`);
  }

  const html =
    `<div style="font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;line-height:1.5;` +
    `border-left:3px solid ${accent};padding-left:12px;">${rows.join('')}</div>`;

  const text = [
    name,
    [title, company].filter(Boolean).join(' · '),
    [email, phone, website].filter(Boolean).join(' | '),
    ...links.map((l) => `${l.label}: ${l.url}`),
  ]
    .filter(Boolean)
    .join('\n');

  warnings.push(
    'This signature uses no images and no tracking pixels by design — both are common spam signals ' +
      'and break in clients that block remote content.',
  );

  return { html, text, warnings };
}
